import React, { useEffect, useMemo, useState } from "react";
import { TextField, Box, Autocomplete } from "@mui/material";
import { phoneCountries } from "../../data/phoneCountries";
import { useTranslation } from "react-i18next";
import {
	detectPhoneCountryFromValue,
	findPhoneCountryByIso2,
	resolvePhoneCountry,
	normalizePhoneForCountry,
	getMaxNationalDigits,
	getNationalDigits,
	getPhoneMetaForCountry,
} from "../../utilities/phoneUtils";
import { createCountryFilter, splitHighlightedText } from "../../utilities/countrySearchUtils";

const PhoneInput = ({
	value = "",
	onChange = () => { },
	onCountryChange = () => { },
	countryIso2,
	countryLabel,
	countryCode,
	allowCountryChange = true,
	disabled = false,
	label,
	error = false,
	helperText = "",
	required = false,
	size = "small",
	margin = "none",
}) => {
	const { t } = useTranslation();

	const preferredCountry = useMemo(() => {
		const resolved = resolvePhoneCountry({
			iso2: countryIso2,
			label: countryLabel,
			dialCode: countryCode,
		});
		if (countryIso2 || countryLabel || countryCode) return resolved;
		return detectPhoneCountryFromValue(value, resolved);
	}, [countryIso2, countryLabel, countryCode, value]);

	const [selectedCountry, setSelectedCountry] = useState(preferredCountry);
	const [phoneNumber, setPhoneNumber] = useState(getNationalDigits(value, preferredCountry));
	const [countryInputValue, setCountryInputValue] = useState(`+${preferredCountry?.dialCode || ""}`);

	useEffect(() => {
		setSelectedCountry(preferredCountry);
		setCountryInputValue(`+${preferredCountry?.dialCode || ""}`);
	}, [preferredCountry]);

	useEffect(() => {
		setPhoneNumber(getNationalDigits(value, preferredCountry));
	}, [value, preferredCountry]);

	const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);

	const filterOptions = useMemo(() => createCountryFilter(), []);

	const phoneCountryOptions = useMemo(
		() =>
			(phoneCountries || [])
				.map((country) => ({
					iso2: String(country?.code || "").toUpperCase(),
					label: String(country?.label || ""),
					dialCode: String(country?.phone || "").replace(/\D/g, ""),
				}))
				.filter((country) => country.iso2 && country.dialCode),
		[]
	);

	const handleCountryChange = (_, option) => {
		const nextCountry = findPhoneCountryByIso2(option?.iso2 || "");
		if (!nextCountry) return;
		setSelectedCountry(nextCountry);
		setCountryInputValue(`+${nextCountry.dialCode}`);
		const normalized = normalizePhoneForCountry(`+${nextCountry.dialCode} ${phoneNumber}`, nextCountry);
		onChange(normalized);
		onCountryChange({ ...nextCountry, ...getPhoneMetaForCountry(nextCountry) });
	};

	const handlePhoneNumberChange = (event) => {
		// Cap from the country's numbering plan, not a fixed 10 -- see getMaxNationalDigits.
		const digits = String(event.target.value || "").replace(/\D/g, "").slice(0, getMaxNationalDigits(selectedCountry));
		setPhoneNumber(digits);
		onChange(normalizePhoneForCountry(`+${selectedCountry.dialCode} ${digits}`, selectedCountry));
	};

	return (
		<>
			<TextField
				type="tel"
				size={size}
				margin={margin}
				label={label}
				placeholder={t("PLACEHOLDER.PHONE_NUMBER")}
				value={phoneNumber}
				onChange={handlePhoneNumberChange}
				variant="outlined"
				required={required}
				disabled={disabled}
				fullWidth
				error={error}
				helperText={helperText || ""}
				inputProps={{ maxLength: getMaxNationalDigits(selectedCountry) }}
				InputProps={{
					startAdornment: (
						<Box sx={{ display: "flex", alignItems: "center", mr: 1, pr: 0.25, borderRight: "1px solid rgba(0,0,0,0.1)" }}>
							<Autocomplete
								options={phoneCountryOptions}
								filterOptions={filterOptions}
								open={countryDropdownOpen}
								autoHighlight
								selectOnFocus
								handleHomeEndKeys
								openOnFocus
								clearOnBlur={false}
								onOpen={() => {
									setCountryDropdownOpen(true);
									setCountryInputValue("");
								}}
								onClose={() => {
									setCountryDropdownOpen(false);
									setCountryInputValue(`+${selectedCountry.dialCode}`);
								}}
								value={{
									iso2: selectedCountry.iso2,
									label: selectedCountry.label,
									dialCode: selectedCountry.dialCode,
								}}
								inputValue={countryInputValue}
								onInputChange={(_, newInput, reason) => {
									// Keep user-typed search text; do not force-reset on each internal reset event.
									if (reason === "reset") return;
									setCountryInputValue(newInput);
								}}
								onChange={handleCountryChange}
								disabled={!allowCountryChange || disabled}
								disableClearable
								forcePopupIcon={false}
								isOptionEqualToValue={(option, selected) => option.iso2 === selected.iso2}
								getOptionLabel={(option) => `+${option?.dialCode || ""}`}
								sx={{
									width: 70,
									minWidth: 70,
									"& .MuiInputBase-root": { p: 0, minHeight: "26px" },
									"& .MuiInputBase-input": { p: "0 !important", fontSize: "0.9rem" },
									"& .MuiAutocomplete-endAdornment": { display: "none" },
									"& fieldset": { border: "none" },
								}}
								componentsProps={{
									popper: {
										sx: {
											"& .MuiAutocomplete-paper": { minWidth: 320 },
										},
									},
									paper: {
										sx: { minWidth: 320 },
									},
								}}
								renderOption={(props, option) => (
									<Box component="li" {...props} display="flex" alignItems="center" gap={1} sx={{ whiteSpace: "nowrap" }}>
										<img loading="lazy" width="20" src={`https://flagcdn.com/w40/${option.iso2.toLowerCase()}.png`} alt="" />
										<Box component="span" sx={{ whiteSpace: "nowrap", fontWeight: 400 }}>
											{splitHighlightedText(option.label, countryInputValue).map((part, index) => (
												<Box component="span" key={`${part.text}-${index}`} sx={{ fontWeight: part.highlighted ? 700 : 400 }}>
													{part.text}
												</Box>
											))}
										</Box>
										<Box component="span" sx={{ color: "text.secondary" }}>
											{splitHighlightedText(option.iso2, countryInputValue).map((part, index) => (
												<Box component="span" key={`${part.text}-${index}`} sx={{ fontWeight: part.highlighted ? 700 : 400 }}>
													{part.text}
												</Box>
											))}
										</Box>
										<Box component="span" sx={{ color: "text.secondary" }}>
											(+
											{splitHighlightedText(option.dialCode, countryInputValue).map((part, index) => (
												<Box component="span" key={`${part.text}-${index}`} sx={{ fontWeight: part.highlighted ? 700 : 400 }}>
													{part.text}
												</Box>
											))}
											)
										</Box>
									</Box>
								)}
								renderInput={(params) => (
									<TextField
										{...params}
										variant="standard"
										InputProps={{
											...params.InputProps,
											disableUnderline: true,
											startAdornment: (
												<Box display="flex" alignItems="center" mr={1}>
													<img
														loading="lazy"
														width="20"
														src={`https://flagcdn.com/w40/${String(selectedCountry?.iso2 || "US").toLowerCase()}.png`}
														alt=""
													/>
												</Box>
											),
										}}
									/>
								)}
							/>
						</Box>
					),
				}}
				sx={{
					"& .MuiOutlinedInput-root": {
						borderRadius: "8px",
						minHeight: 36,
						opacity: disabled ? 0.8 : 1,
					},
					"& .MuiOutlinedInput-input": {
						p: "8px 0",
					},
				}}
			/>
		</>
	);
};

export default PhoneInput;